import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Crown, LogOut, Trash2, BellOff, Hash, Users, MessagesSquare, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { timeAgo } from "@/lib/format";
import { cn } from "@/lib/utils";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const KIND = {
  bloq: { label: "BLOQs", icon: Hash, route: (s) => `/app/bloqs/${s.id}` },
  circle: { label: "Circles", icon: MessagesSquare, route: (s) => `/app/circles/${s.id}` },
  channel: { label: "Channels", icon: Users, route: (s) => `/app/channels/${s.id}` },
};

function SpaceRow({ space, onAction, busy }) {
  const navigate = useNavigate();
  const K = KIND[space.kind] || KIND.bloq;
  const owner = space.is_owner || space.role === "owner";
  const action = space.kind === "channel" ? (owner ? null : "unsubscribe") : owner ? (space.kind === "bloq" ? "delete" : null) : "leave";
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-white/8 bg-bloq-s1 px-3 py-2.5" data-testid={`space-row-${space.id}`}>
      <button onClick={() => navigate(K.route(space))} className="flex min-w-0 flex-1 items-center gap-3 text-left" data-testid={`space-open-${space.id}`}>
        {space.icon_b64 ? <img src={space.icon_b64} alt="" className="h-10 w-10 rounded-xl object-cover" /> : (
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-bloq-s2 text-lg">{space.emoji || <K.icon className="h-4 w-4 text-bloq-muted" />}</span>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-sm font-semibold text-white">{space.name}</span>
            {space.verified && <ShieldCheck className="h-3.5 w-3.5 text-bloq-mint" />}
            {owner && <span className="inline-flex items-center gap-0.5 rounded-md border border-bloq-purple/40 bg-bloq-purple/15 px-1.5 py-0.5 text-[9px] font-bold uppercase text-bloq-purple"><Crown className="h-2.5 w-2.5" /> Owner</span>}
            {!owner && space.role && space.role !== "member" && <span className="rounded-md border border-white/15 px-1.5 py-0.5 text-[9px] font-bold uppercase text-bloq-muted">{space.role}</span>}
          </div>
          <div className="text-xs text-bloq-muted">
            {space.handle ? `@${space.handle} · ` : ""}{space.member_count ?? 0} {space.kind === "channel" ? "subscribers" : "members"}{space.last_activity ? ` · active ${timeAgo(space.last_activity)}` : ""}
          </div>
        </div>
      </button>
      {action && (
        <button
          onClick={() => onAction(space, action)}
          disabled={busy}
          className={cn("flex h-8 items-center gap-1 rounded-full border px-2.5 text-[11px] font-semibold disabled:opacity-50", action === "delete" ? "border-destructive/40 text-destructive hover:bg-destructive/10" : "border-white/12 text-bloq-muted hover:text-white")}
          data-testid={`space-${action}-${space.id}`}
        >
          {action === "delete" ? <Trash2 className="h-3.5 w-3.5" /> : action === "unsubscribe" ? <BellOff className="h-3.5 w-3.5" /> : <LogOut className="h-3.5 w-3.5" />}
          {action === "delete" ? "Delete" : action === "unsubscribe" ? "Unsubscribe" : "Leave"}
        </button>
      )}
    </div>
  );
}

// cc_22 "My spaces" hub — GET /me/spaces in one call; actions reuse the existing leave/delete/unsubscribe routes.
export default function MySpacesPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(null); // { space, action }
  const { data, isLoading } = useQuery({ queryKey: ["my-spaces"], queryFn: api.mySpaces });
  const mut = useMutation({
    mutationFn: ({ space, action }) => {
      if (action === "delete") return api.deleteBloq(space.id);
      if (action === "unsubscribe") return api.unsubscribeChannel(space.id);
      return space.kind === "circle" ? api.leaveCircle(space.id) : api.leaveBloq(space.id);
    },
    onSuccess: (_, { space, action }) => {
      qc.invalidateQueries({ queryKey: ["my-spaces"] });
      qc.invalidateQueries({ queryKey: ["bloqs"] });
      qc.invalidateQueries({ queryKey: ["chats"] });
      qc.invalidateQueries({ queryKey: ["channels"] });
      toast.success(action === "delete" ? `Deleted ${space.name}` : action === "unsubscribe" ? `Unsubscribed from ${space.name}` : `Left ${space.name}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Couldn't do that."),
    onSettled: () => setConfirm(null),
  });
  const groups = [["bloq", data?.bloqs], ["circle", data?.circles], ["channel", data?.channels]];
  return (
    <div className="h-full overflow-y-auto bg-bloq-bg" data-testid="my-spaces-page">
      <div className="mx-auto max-w-2xl px-5 py-6 sm:px-8">
        <div className="mb-5 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="rounded-full p-1 text-bloq-muted hover:text-white" data-testid="spaces-back-btn"><ArrowLeft className="h-5 w-5" /></button>
          <div>
            <h1 className="text-2xl font-bold">My spaces</h1>
            <p className="mt-0.5 text-sm text-bloq-muted" data-testid="spaces-counts">
              {data?.counts ? `${data.counts.owned} owned · ${data.counts.total} total` : "Everything you own or belong to"}
            </p>
          </div>
        </div>
        {isLoading ? (
          <div className="flex justify-center py-20 text-bloq-muted"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : (
          <div className="space-y-6">
            {groups.map(([kind, list]) => (
              <section key={kind} data-testid={`spaces-section-${kind}`}>
                <div className="mb-2 px-1 text-[11px] font-bold uppercase tracking-wider text-bloq-muted">{KIND[kind].label} · {(list || []).length}</div>
                {(list || []).length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-white/10 px-4 py-4 text-center text-xs text-bloq-muted">None yet</div>
                ) : (
                  <div className="space-y-2">{(list || []).map((s) => <SpaceRow key={`${kind}-${s.id}`} space={{ ...s, kind }} onAction={(space, action) => setConfirm({ space, action })} busy={mut.isPending} />)}</div>
                )}
              </section>
            ))}
          </div>
        )}
      </div>
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="border-white/10 bg-bloq-s1 text-white" data-testid="space-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.action === "delete" ? `Delete ${confirm?.space?.name}?` : confirm?.action === "unsubscribe" ? `Unsubscribe from ${confirm?.space?.name}?` : `Leave ${confirm?.space?.name}?`}</AlertDialogTitle>
            <AlertDialogDescription className="text-bloq-muted">
              {confirm?.action === "delete" ? "This permanently removes the BLOQ, its chat, members and recordings for everyone." : "You can always come back later."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/10 bg-transparent text-white hover:bg-white/5" data-testid="space-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => mut.mutate(confirm)} className={cn(confirm?.action === "delete" ? "bg-destructive text-white hover:bg-destructive/90" : "bloq-gradient text-bloq-bg")} data-testid="space-confirm-ok">
              {confirm?.action === "delete" ? "Delete" : confirm?.action === "unsubscribe" ? "Unsubscribe" : "Leave"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
